import { Query } from '@apollo/client/react/components';
import { WithApolloClient, withApollo } from '@apollo/client/react/hoc';
import classnames from 'classnames';
import { Dayjs } from 'op-utils/dayjs';
import dayjs from 'op-utils/dayjs';
import { ROMiniRegoHeader, ROTable } from 'op-components';
import {
  ChangeDocumentTypeMutation,
  ChangeEncounterDateMutation,
  CreateUKAttachmentMutation,
  GET_UPLOAD_DETAILS_QUERY,
} from 'op-components/FileUploadField/FileUploadQueries';
import { PatientAttachments } from 'op-interfaces';
import React, { Component } from 'react';

import { Prompt } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { BaseDatePicker, BaseAutocomplete, ErrorInfo, SelectOptionType } from 'shared-components/components/FormFields';
import { Region } from 'shared-components/enums';
import { ListData } from 'shared-interfaces';
import './RORegAttachments.scss';
import ModalInfo from 'shared-components/components/Modals/ModalInfo/ModalInfo';
import { checkFileExtension } from 'op-components/FileUploadField/helper';
import { Logger } from 'shared-components/utils';
import { Box, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { getOptionByValue } from 'op-pages/RO/Careplan/DiagnosisPages/Utils';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';

const logger = new Logger('roRegAttachments');

const REACT_APP_REGION = import.meta.env.REACT_APP_REGION;

interface State {
  selectedFiles: any[];
  documentType: string | null;
  dropzoneClass: string;
}

interface Props extends WithApolloClient<{}> {
  isActive?: boolean;
  patient: PatientAttachments;
  user: any;
  refetchCallback: () => void;
  documentTypeRefData: ListData[];
  fileUploaded: boolean;
  onIncompleteFileUpload: any;
  onCompleteFileUpload: any;
  onCloseUploadErrorModal?: any;
  onOpenUploadErrorModal?: any;
  uploadInfoErrorModalOpen?: boolean;
  onDelete: (attachmentPk: string) => void;
}

const fileSizeErrorMsg = 'Max file size is 20MB';
const attachmentsAmountErrorMsg = 'Patient can only have up to 10 attachments';
const FORGOT_UPLOAD_TITLE = 'Forgot to upload?';
const FORGOT_UPLOAD_TEXT = 'Please upload the last file you added or remove it from the list.';

class RORegAttachments extends Component<Props, State> {
  private readonly fileInputRef: React.RefObject<HTMLInputElement>;
  public constructor(props: Props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      documentType: '',
      selectedFiles: [],
      dropzoneClass: 'dropzone',
    };
  }

  uploadFile = () => {
    // @ts-ignore
    this.fileInputRef.current.click();
  };

  public componentDidMount() {
    window.addEventListener('dragover', (event: DragEvent) => {
      event.preventDefault();
    });
    window.addEventListener('drop', (event: DragEvent) => {
      event.preventDefault();
    });
  }

  private selectFiles(files: File[]) {
    const { selectedFiles } = this.state;
    const file = Array.from(files)[0];
    selectedFiles.push(file);
    this.setState({ selectedFiles: selectedFiles });
  }

  private removeFile(file: File) {
    const { selectedFiles } = this.state;
    const filtered = selectedFiles.filter((oldFile: File) => {
      let include = true;
      if (file.name) {
        if (file.name === oldFile.name && file.lastModified === oldFile.lastModified) include = false;
      }
      return include;
    });

    this.setState({ ...this.state, selectedFiles: filtered });
  }

  private handleDocumentTypeChange(attachmentPk: string, documentType: string) {
    this.props.client?.mutate({
      mutation: ChangeDocumentTypeMutation,
      variables: {
        attachmentPk,
        documentType,
      },
    });
  }

  private onDragOver = () => {
    this.setState({ ...this.state, dropzoneClass: 'hovered' });
  };

  private onDragLeave = () => {
    this.setState({ ...this.state, dropzoneClass: 'dropzone' });
  };

  private handleDrop = (event: any) => {
    const newFiles: any = [];
    if (event.dataTransfer.items[0].kind === 'file') {
      newFiles.push(event.dataTransfer.items[0].getAsFile());
    }
    this.selectFiles(newFiles);
    this.setState({ ...this.state, dropzoneClass: 'dropzone' });
  };

  private handleDateChange(attachmentPk: string, encounterDate: string | Dayjs | null): void {
    if (encounterDate == null) return;
    const { refetchCallback } = this.props;
    if (!this.props.client) {
      throw new Error('client is undefined');
    }
    const dateStr = typeof encounterDate === 'string' ? encounterDate : dayjs(encounterDate).format('YYYY-MM-DD');
    this.props.client
      .mutate({
        mutation: ChangeEncounterDateMutation,
        variables: {
          attachmentPk,
          encounterDate: dateStr,
        },
      })
      .then(() => refetchCallback());
  }

  private handleDocumentRemoval(attachmentPk: string) {
    //const { refetchCallback } = this.props;
    this.props.onDelete(attachmentPk);
  }

  public render(): JSX.Element {
    const { patient, documentTypeRefData, user, refetchCallback } = this.props;
    const { selectedFiles, dropzoneClass } = this.state;
    const attachmentCount = patient.attachments.length + selectedFiles.length;
    const fileLimitReached = attachmentCount >= 10;
    const tableHeaders = ['', 'File name', 'File size', 'Document type', 'Encounter date', 'Progress', 'Actions'];
    const colWidths = ['3%', '25%', '5%', '22%', '25%', '10%', '10%'];
    return (
      <div className="main-container ro-uk-full-width reg-attachments-wrapper">
        <ROMiniRegoHeader
          title={'Attachments'}
          summary={
            'Please attach any related patient documents. Attachments will be submitted to MOSAIQ when patient registration has been submitted.'
          }></ROMiniRegoHeader>
        <ROTable tableHeaders={tableHeaders} colWidths={colWidths}>
          <tbody className="attachment-file-list">
            <TransitionGroup component={null} className="attachment-file-list">
              {selectedFiles.map((file: any, key: number): JSX.Element => {
                return (
                  <CSSTransition key={file.id} timeout={500} classNames="attachment-item">
                    <ApolloUploader
                      handleFileDelete={() => {
                        this.removeFile(file);
                        this.props.onCompleteFileUpload();
                      }}
                      documentType=""
                      refetchCallback={refetchCallback}
                      documentTypeRefData={documentTypeRefData}
                      key={key}
                      file={file}
                      patient={patient}
                      user={user}
                      fileUploaded={this.props.fileUploaded}
                      onCloseUploadErrorModal={this.props.onCloseUploadErrorModal}
                      onOpenUploadErrorModal={this.props.onOpenUploadErrorModal}
                      uploadInfoErrorModalOpen={this.props.uploadInfoErrorModalOpen || false}
                    />
                  </CSSTransition>
                );
              })}
              {patient.attachments.map((attachment: any, key: number): JSX.Element => {
                return (
                  <CSSTransition key={attachment.id} timeout={500} classNames="attachment-item">
                    <TableRow
                      key={selectedFiles.length + key}
                      attachmentPk={attachment.id}
                      downloadUrl={attachment.url}
                      filename={attachment.filename}
                      filesize={attachment.filesize}
                      errors={[]}
                      submittedToMosaiq={attachment.submitted}
                      changeDocument={(e: string) => {
                        this.handleDocumentTypeChange(attachment.id, e);
                      }}
                      removeDocument={() => {
                        this.handleDocumentRemoval(attachment.id);
                      }}
                      handleDateChange={(value: any) => {
                        this.handleDateChange(attachment.id, value);
                      }}
                      encounterDate={attachment.encounterDate}
                      documentTypeRefData={documentTypeRefData}
                      documentType={attachment.documentType}
                      btnTitle="Uploaded"
                      loading={false}
                      allowDelete={!attachment.submitted}
                      disableBtn={true}
                      disableSelect={attachment.submitted}
                      progress={100}
                    />
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
          </tbody>
        </ROTable>
        <Stack
          onDragOver={this.onDragOver}
          onDragLeave={this.onDragLeave}
          style={fileLimitReached || selectedFiles.length > 0 ? { backgroundColor: 'lightgrey' } : {}}
          className={dropzoneClass}
          onDrop={fileLimitReached || selectedFiles.length > 0 ? () => {} : this.handleDrop}
          id="dropContainer"
          onClick={this.uploadFile}
          justifyContent="center">
          <Box>
            <div className="cloud-image-div">
              {selectedFiles.length === 0 && (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink">
                  <rect x="0.966553" y="0.00231934" width="24" height="24" fill="url(#pattern0)" />
                  <defs>
                    <pattern id="pattern0" patternContentUnits="objectBoundingBox" width="1" height="1">
                      <use xlinkHref="#image0" transform="scale(0.00173611)" />
                    </pattern>
                    <image
                      id="image0"
                      width="576"
                      height="576"
                      xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAkAAAAJACAYAAABlmtk2AAAACXBIWXMAACxLAAAsSwGlPZapAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAADHFSURBVHgB7d2xkxz3lSf498sGQW6soR5vrVPTG+vYvBElSmuw6Y1H0DyLoMEIoEUA1bTuLDa8swYFkAIQIYNNbz2A1u1abFpDzTACTW/PYukvmJaxEUQDnb/LrGpQEEUQ6M6syqzKzycC7AZGQ5EQKutb773f+0UAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/IwVAy0ajSxuz747XI9bWZ9+n9TjO6z/7/7iWDiPy4VO/Mqn/Mh7fnQRAiwQg4IXMQk0VaI7XNlMqflH90qspxy9y5I3qSbIx+0+ljZirPKn+Ow4j58Pq4XWYU/pzrr5GkQ9OgtNEWAJehAAE/GA0GlUVmu834zg2qpCzmXL+ZU6xOf9g07qDOiRFSt9W33+Xi7L6+srBeDw+DIAQgGCwphWd49iqg04VFl6rwkIVdGI9VltdPTqYBaP0IBdH347HfzwIYHAEIBiAWWXn4VYq09aAws6Ly3m/DkW5qL5+nw/Gd7XRYNUJQLCCpoHn+PsL0+pO5HeWsIXVtUnKsZ9T2s9Hx18JRLB6BCBYAU9md1JZXBB45mIaiMq1/EXEy/tmiWD5CUCwpOrQk8qj96r2zQUtrQWbtczu56PyC9UhWE4CECyRenC5KIv3cs5b1RvwVtAHk+rHF7l4tGegGpaHAAQ9J/QslUkIQ7AUBCDooR+1t7aCZTSpfoy1yaCfBCDokaras5WO08dCz4qZzgwVezdu/eHzAHpBAIKO1dWeojy6lqtvwyDzqpudJntUXlcVgm4JQNAR1Z6BUxWCTglAsEA/zPZEXKx+bAZMZ4XSroWLsFgCECyANhcv4LBqj93XHoPFEIBgjgYcfA6rHs90W3LO09NQJ7+WnrNBOW/88G1K6yly9XuW6t+3QYXGKgjtCUIwXwIQzEG9uyeVabd6ib0XKyVPch1ichykFH+pfuG7nMu/RB1yHlc/XnnlcF7XREyv+/j++/U4Fxt1KEpF+mUVLP9herlrFZCqf56NVbsCRBCC+RGAoEUrEnwOcz2XMgs5B7nMf47H+WCe4aZNo+0PNqM4tzENSPXyyGk4Wu6rQgQhaJ8ABC2Ytrryo4+rN9xRLJfD6p+5Cjrp26qScxCPYn9V32SfCkZb1b/za8sYigQhaI8ABA0s4YxPFXji/rSy8/jRV+Pbw76uYRqK1tY202wVwVvL0kIThKA5AQjOaOfq9rXqy270O/j8NfC4kuG56hZmHMfWkgSiw+oBPv6XW7evB3BqAhCc0nSBYZk+6+ubY54u2EtfRVnujz+9ux+cWV0hSudeeqv6Pb2Q+ruwclLvEbJQEU5HAIIXNB1wPq6CTw/fCOvQU71B38/F+c+XYVB5GT2pDlWPzXeq3+sL0Td1le9h+a4qH7wYAQieo69zPtNKT+TPY+2V+0LPYk2P5B9/f6GPYSilPC4f5puCEPw8AQh+Rt/aXSo9/fNUGHqvR22yibYY/DwBCH5Cz46113t5bsbjR/eHfmqr72Z7oIp3qqg66kNodloMnk0Agh8ZfXj5Qirqqk+37a5piyvn6waZl9Pow6p6WKSLPViKWe96uj7+5M44gB8IQHBiWvU5fvhZ7namQ7VnxUyHp8tilCK/02lVqArU+VF+XzUIZgQgiF5UfWbBpzg/NtuzukZXLl1MKX3cYRBSDYITAhCD1vWsz6zNFTfHn965HwxGHbirp++1roamzQaBAMSAdXnCy3wPtXpOKFK61tFR+omTYgyZAMQgnVxjsfA2gODDT5mdHku7XQxMV28Cu67TYIgEIAalq23Ogg8v4qQi9HEHrbFJPirf1hJjSAQgBqOjltdEm4HT6mhYeuLPKkMiADEIHbS8pqe6xrdu7wacURdBSEuMoRCAWGmzU15HN3KOi7E4N3Nxftdxdtoyurq9Wz2sP45FsTOIARCAWFkn1xLcq77djAWYzvkcP96xwJB56GBQ2lwQK00AYiXN5n2m4WcRiw2rSk8amZ1gERbcFjvMZVUJsqeKFSQAsXIWPO+j3UUnFtkWMxfEKhKAWCnVm8LH9cM65m+Sy/J9x9rp0klb7MtFVINSyuN/uXlnJ2BFCECshAUPO6v60CsLrAYdVH/23/Znn1UgALH06vCTyqPqU/Dch51VfeitBVaDDEezEgQgltoCH/qqPiyFBVWDhCCWngDE0lpQ+HEKhqUz2v5gM507d2/Or41JfvzoXWsfWFZFwBJaRPip9/pUn3JfF35YNnUoyUV+u/pTPM/VDBvp3Etf1mErYAmpALF0Zp9uX6pnfua54+f6DddYsAIW0BJTJWUpCUAslQWEn+phXr5r0JlVsph2cbpoGSjLZC1gSSwg/BxMBztv3zXTwEr5+utvDt/87a++KMrYiJT+Mebjwm9/8+vJ13/6928DloAKEEthAeHHKS8GYf4tMZUgloMARO/NO/zknHfGn9xZ1NUZ0LnRlcuj2X1i8/pAIQTRfwIQvTbn8GPeh8Ga91xQ9dp622uLPhOA6K05P6AtcmPw5vwaO8yPH71tTxB9ZQ8QvTTnB/OB8AMR4/HdSS5efr3qA+9H+9btCaLPVIDonXmGn5zy/Ugvv2/YGf7W6Or2uHpDuBbtU22llwQgemV2senDB/Op/OTPb9y6czGAnzTHE2JCEL2jBUavzG51TxvRvuvCD/y88a3bu7l6rUT7NtL54l79ASegJwQgeuOja7+/UX2Zx7yAay3gBc0xBG0Wxw8/C+gJm6Dphar0Xpfd/69on/ADp/T1n/59/81f/9OfU0oXok0p/ePv3vzV+r/+6Zv/EdAxAYjO7VzdvpYi/p9on/ADZ/T1v31zMJcQFOnNN3/9q79Uf/+vAzpkCJpOnSw6fBDtE36gBaMrly6mVLTeurIoka4JQHRmjsfdhR9o0ZxC0GE+Kl93MoyuGIKmM8IPLIfxJ3f3ci7fj3atp/PFl06G0RUBiE7MTnyljWiX8ANzMg1B7Z8O23AyjK4Ygmbh6qHn6stutEv4gTmbng77zRspRWxFW1L6R0PRdEEAYqGmcz85/fdo1XTD8yiAuZtHCEop/fObb/zTV1UImgQsiADEwjw19Nxazz/nvD/+5M67ASxMHYJ++5tfvVq9lltbXFqFoK03X/+nL77+5hv39LEQZoBYmCr87LY89zOJtZeFH+jAydUyB9GejfRSMg/EwghALMRs7ie9F+2ZXa7oVnfoTC7Ov139dRJtqapAoyuXtbNZCHuAmLtZ66v4Ltpjfwj0xMnru15m2l5r25JEFkAFiLmbzf20J5f5feEH+mE8vjupAkurrehUFJ/ZD8S8CUDM1eyS07QR7bk+/vTO/QB6o67W5Jx3oj0bRX74ccAcOQXG3FSl8a2U0160JKfYG9+63eZDFmhJvcfnzd+88Q8p4s1oRXrT0XjmSQWIuajL11Xrq80THZNI54Uf6LHqA0o9wNzaybCqFXZPK4x5EYCYiyI/arP1dejEFyyHXNTzQK2dDFt3VQbzIgDRurr1lXNu8ShrGhl6huUwG4rOrV2cmlO6MPrw8oWAlpkBolWz1tfx/9vituf6jq+bASyNem6nzesyUkpvvvnb//r5119//X1AS1SAaFd5dK3F1tfEBaewnMb1azfn/WiHU2G0TgCiNdOFaO3d8j6d+wlgaeW1aSusldm9nNNo9OGlrYCWCEC0ps2Fhznn6+Z+YLm1PQ+UiuJGQEvMANGK0ZVLF1MqLkYLZvt+7vzfASy9r//tm//Z4n6g//Lb37yR6tvoAxpSAaKxaesrpbb685N4WF4PYHUU53dbPBp/bXTp0kZAQwIQjVWtr932Bp/TrtYXrJZ6h1eLrbD14qXCQDSNaYHRyLT6k4tWFpWdXHWh+gMr6ORofDutsBSbrsmgKRUgGknHrV13ofUFq67FVliLbXcGSgDizOrB5+optBWt0PqCVddqK6x69uxc/f17AWeUAp6jbnNVf1Q2U5l+mXJsVq2qzeqXq1+LVrY9T1tfN2+3dlQW6Ledq5f3qmdKG+Flkovzr7snkLM4F/CU2c3LD7eqsLOVcv5lris85V+DTm4/Mh9qfcGw5OLlUSqP3onmH6I2qr9Pfe/gbsApqQAN3LS6cxxbRRRv5ZS3WrzG4kVdd90FDM/oyuVRSqmNxYaHVRXoVVUgTksAGphZhef7qp1VXKg+h73TQeB5Wn3X16sBDNLOlctftjRH6IMUp6YFNgCzG9qP3oucL0R5tBlRnJSdu82/ucw7AQxWfeVNaicAXauec2NVIE5DBWhF/U3oae2kVnsMPgO1FgeiVYE4FQFohfQ99DwtH5WvOvYOnDy3vovmA9FmgTgVm6BXwGh0aet3b7xxI6IcVz+tw89G9NvN8R/u/LcABu/rr7/+/s3fvPGfqk/jW9HMKykfP3RRKi9KBWhJ1Z+aivLoWq6+jZb28SzIpKr+vK36AzyhCkQXVICWTF3t+e2v39hNubxT/fSfqx+vxBKphx7Ht+/+9wA4Ma0C/fpXD1NK/xzNqALxwlSAlkQdfNJx+rjvsz3P4dg78Ew7Vy9/18JqDlUgXohj8D1X37c1vfSvTBvLH1fTbgA8w8mx+KYXLK/H8cOL1ddxwM9QAeqpH4JPpI1YDao/wHO1VAXyvOG53AbfM3XwqR8AKRWfrVD4CdUf4EXUVaBobmP04aWtgJ+hAtQT0xmfMq1Y6PnBQfVp7PUAeAGtVIFy3r/xyZ23A55BAOrYCgw3H+aq3Bw5DlKKv1Q//y6X+c9RPp5EuXbouDtwWrMRgKLpLFDksnx7/Ond/YCfIAB1pL6FvThON3JKF2JJVEHn4CToHExDzuN8IOAA87Bzdfs/ovleoJtV9XkU8BMEoAVbngWGeZJz2p+GncePvorz/3niWCmwKKOr27vVG9TH0Ywj8TyTY/ALNJvzefhZjrQR/XOYc9yPKL+KR7GvsgN0qjg/jurDYjT7oOhIPM+kArQA06pPfvRxzrlXpdjqn2c/UvoqynJfnxzom1aqQIaheQYBaM5GH16+kIrpYq9etLvq0JNSul+VhT9XFgb67OSOsP+IhgxD81O0wOZkWvU5rtpdPRhyFnqAZVQ/r3auXN5veko2FUX9HN4PeIoANAezWZ+je1X46bLqUwedz6tPPvd98gGW1cn1GFvRzHsxO3gCP9ACa1EfZn2mcz05bsa5l/dVe4BVsHN1+0H1ZTMa0Abjx1SAWjLa/mAzlQ/vdXXCK6fYi+Pycy9wYNXkiC9SwwBU9cHqKtB+wAkVoBZUn07qo5q7sfhB53oL8836uKhqD7CqToahv4tmz1g7gfgbAlADHba8BB9gUD66sn2vqnQ3OlSSc7w//uT2XkBogZ1ZfZXFdNC5aVn2dAQfYJDKXN5MqWgUgFLkug22FxAqQGcyO+VV3IvFtbwEH2DwWrgfTBuMH6gAndJ03qdc3Fr16XDzw/K6qymAoas/CDbcDL0ex0d1FWkvGDwB6BQ+uvb7G4ua95kdZ8/XneoCOFGW+1EUTS9IfSsEIEIL7IUseKvzYb34a/zJHZf3AfzIztXL31VvXRtxdtpgTBXBzzoZdv5yQeHn5vSFKfwA/KQc6fNoZj0ef7/Iwyv0lBbYz5iFn/Rl9e1GzNckl+X72l0Az9FCG8zdYNRUgJ7hr+EnbcR81VWf14UfgOebPSvzJJp5Jxg8AegnzK61KB7MOfzUVZ+3b9y6PdKLBnhxVRvsi2hmY3Tp0kYwaALQj0zDz7mX6rbXPHf8qPoAnFVZ3o+mXlrIXCc9JgA9ZQHhpz7htaPqA3B2Jx8emz1DU94KBk0AOrGA8DPJR+XrTngBNJcjN2qDpSjeCgZNAIqFhJ9Zy8s2Z4B21Mtim1kfjT5wHH7ABn8Mfnbaq5hb+KlbXqo+AC1be+V+lEefRRPH57aqvx4EgzToCtBTe37mEX4O61Newg9A+07mKBuFl+Q4/KANtgI05z0/9bzP21peAPOTI76qQszZ21gpbe1c3a7+NlWQyvmw+vm31fff5aKsvr5y4LDKahvkXWD13V6pfDivPT8HuTj/thcOwHyNPrx8IRXpXszPQcp5klPar0PReGx1ySoZZAWovttrPuEnf37j1p2LAcD8nXt5P8qjmKPNKvzUFaYLqSyiqhbNhq+rSlEViO6rEi23wVWAPrr2+xs551G07/qNW7d3A4CFqUJJVc1v0AZrahaI7ufi0Vfj8R8NVC+RQQWg0dXtj6t/4d1on/AD0IHquT6unuvXoh8m1Y8v6uqQdln/DSYAzbFXLPwAdGR05dLFlIpmx+HnY5Jy7Jdrj26qDPXTIALQya6fukza9nF34QegQyfP9++i36oAlMb56Pgrp4P7Y+UD0ByPuws/AD2wc3X7P2K+F1i3pqoK7ZVr5edaZN1b+UWIxXG6IfwArLCcl6bFlFNcrG8fqELbdztXf/9e0JmVrgDNaehZ+AHokZ4NQp/WpPpn3yuPqqqQ9thCrcWKml5wWqz9t2iX8APQM2/++p/+S0rpQiynunW3ldbShd/9+o3N3/wf//Tt1998Y7fQAqxkBWhOcz83q/Azj/1BADSwJIPQL2w6J/SovK4iNF8rGYB2rl7eq/7VWuut5pz3x5/ceTsA6KVlGoR+QbPWWHH+pm3T87FyQ9DVi+Bam+En6sVWay+/GwD01xINQr+gjRyxm8qjB4al52OlAlBdBo12h55nt7pL3wC9Vl9YWn1ZxWf1RvVvt1efGhuNPujuyo8VtFItsLbvhMmPH70+vm2DJ8AyGV2afhiur/uefU2xkVLxi6qi8g9Vpei16lfWU6r/b2kjloz5oPasTABq+8h7znln/MmdcQCwsuoTw1Gc24gibU7DUUobqcvLVV/MpHr73r1x6w+fB2e2EgFoDicAHHcHGKjRaLQej7+vglGxVYWit1JKdSDq34B1ioP8sHxXNehsViIA7Vy5/GWV2reiHQdV+Hk9AODE6MNLW6koXqu6AxdSe+83rai7H/9y6/b14FSWPgC1fBPwbOhZmgbgGWYVoodVIIoL1dvoO9GH6lDO+/lRft/714tb6gDU9sLDXOZ3x5/euR8A8IJGH16+cBKGuj6uPjEb9OKW+iqM3/76jXH1P/ZWtOP6+JM7dwMATuHrf/vmf379p2/uv/nb/3ozykf/X0xPmaWNWLy6EnXhd2/+av1f//TN/wh+1tJWgFoefJ7cuHX71QCAFpx0KHY7bJEZ6XiOpV2EOGt9taP+QxIA0JLx+G71wfrOxVyUr+dcvl+900xisTbS+eLLuj0X/KSlrAC1PPjsyDsAczd770ofL3oBo1NiP20pA9DO1cvftfQHSOsLgIXqIgillMf/cvPOTvCDpQtAbW58rlpfr+qPAtCFDoLQQfW+Z3HiiaUKQC0fe9f6AqBz1Qf73RT5vQUFIcPRJ5ZqCLrIa9dabH3tBgB0bFy9H+Uiv131JRaxv2c2HL3tZvmlqQC1eezdwkMA+qjtBb8/4zA/fvT2+PYfD2KglqYCNNun0FxOsSf8ANBHJ8fnX80R8z61tZ7OvfRg5+rvu95e3ZmlCEB1Im5txfjD0lFAAHpt1hYrX53//qC8N9QQtBQBqK3qT9TXXRj8AmAJLK4aNMwQ1PsZoJPZnwfRfJW4nT8ALKXFzAali0O6SLX/FaDjVK/xbuEeldaqSACwUHU1KBcvvz7fk2J5b0inw3ofgFKKa9HcZEipFpbB6FI92we8qPF4fDi9XyznuW10TudeGswR+V4HoHpLZjvlPtUf6JN63iCdLx58dGX7s9Fo1MVN2bC0xp/cGefHj16f04B0fTrs3hA+oPQ6AM1WhDem+gM9Mhu2zHvVt+s5xcVUHn0pBMHp1Pt7TpYnTqJ9s2WJKx6CehuARqNLW6o/sFp2rm5fOwk/T9uchiAtMTiVOc8F1SHo3ip/OOltAEpluhjNqf5AT9QXGVdfxs/4P28O4RMntO2HuaD5HJXfLI6PbsSK6mUAam/xoeoP9EEdflLE7nP+Y4Mou8M8TBcnziEE1W3qj2YfXlZOPytAx7EVzan+QA+8YPh5QgiCM5pbCKpev6u4KLGXAaid4WfVH+jaR9d+f+MU4ecJIQjOaBqCcvl+tC6PV+012bsA1NLw82E+Ov4qgM58dG37s5zzKM6mDkEPhrSUDdoy/uTu3hxC0Pr0g8kKDUX3LgC1Mfxc9Szvu/MLulE/IGfhJy5GM+tDWsoGbZpTCNoo8sOVmQfqYwvsrWjKje/QiTr81EfaWwg/T9Qh6MFQb6uGJmYhqN2t0Tmn0ejK5bNWdnulVwFo9OHlC43bXznvq/7A4j0JP9W3c6jYDPO2amhqujW65cHoek53FeaBehWAUhEXoqlU7AWwULObqh8+iLmEnyfy3qoex4V5msPpsPX0Uvosltxa9Mhvf/Pr+jf0lTi7wxu3bv+fASzMLPykL9vZ3P5cW7/7zRvxr3/6d4cc4BS+/tO/7//2N796tXqdtvMhJaWNN3/9q798/W/ffB1LqjcVoFn7KxpNl9fDzwEszILDz1S9k0QlCE4vFy/XszsH0ZKqFXZjmQ8p9CYAtdL+Oi4tPoQF6SL8PCEEwenV12bkony3zQtU09q5pb0qo0czQOmdaGYy/vTufgBzV3/qS2XxoIvw88Q0BF27vLL3FME8TC9Qffy4CkFxGG1IaWtZT4X1IgDNlh82bn/tBzB39eu13s8TDV+zbaiP5H50ZXvphzFhkca3/3iQc25tKHpZT4X1owJUFi2c/np0M4C5qo+iV5WfXoSfJ+rLGneuXF6pDbUwbyfH49t631zKU2G9CECp+fLDyXj8x9YGu4C/N9vDk/eij6oyfL2DSAiCUyjO77Y2D1S3wj6cdnOWRucBqB6kjIa7Q7S/YL5G04Hjnoafv9qsQtADl6jCi5kORbc4D5SKYqmqQN1XgI5jK5py+gvmpg4/Z7jRvStukodTaHkeaGOZBqI7D0CpKptFM4dOf8F8LFn4eUIIglOo54Hqa6SiBdOB6CVpRfdhBqjR/E+O0kZYmIMlDT9PCEFwCnkt1zfHt9EKq+8EXIoqUKcBaDb/kzaigRRr9wJo1UfXtj9b4vDzxCwELfGmWliU6X6g9lph15ahCtRtBehx8ztJ8tGxChC0qA4/OcfFWA0b9c4iIQier8VW2FJUgboNQEXj+Z/J+O7dSQCN1Z/Y6n06KxR+nlifhqDZfYPAzxhSFajTAJRyfi0acPwd2lE/qOo9OtH8UEJfraci3ZvtMgKepT5U1NKCxN5XgbqtADV82Kac9gNoZHap6cMH0XAf13LIe8t6bxEszHRBYisD0b2uAnUWgEbb283nf4qjbwM4sy5vdO9KSumGm+Th2aYLEgdQBequAlTkjWjm0PUXcHZDDD9PTG+SF4Lgmca3bu+2dE1Gb6tAHQaghrMGOQs/cEZDDj9PCEHw83IZO9Hcehw/vBg91FkAajoAXdWxtb/gDOoj4aksHgw5/DwhBMGzjT+9c7+NY/GpSL08fNBdBSg13AG0Fl8GcCrT8HPupfq149b0E9MQdGV7qS5xhEVp5Vh8js0+3hTfSQA66Qc2egDn/OjPAbyw+gi48PPTcoqLH125fG9Z7jCCRZnetdlGFSil3lVaO6oAfd/4BJgBaHhxs/03eS+En2fKKV2odyEJQfC3cm7hRFhKW317bXUTgB6nZr8JKYQfeEE7V7evnYQfnm9zGoJcogo/mM4CtXAirG9H4rsJQEWz+Z+cS+0veAGj2YDvODiNTTfJw9/KkT6PpnJ+K3qkoxZY0x1AhQoQPEcdflbgRveubAhB8JTifP1Bqtl26LoN1qNh6E4CUMrxy2gixySAZxJ+WiEEwYmT7dCNq0CpKHpzKXE3FaAUG9FELicB/KSPrv3+hvDTGiEInijL+9Fcb3YCddQCSxvRxGMVIPgpH13b/izn7LLPdtUh6EG9QylgwFo6Er/elzbYwgNQG8fgxnfvTgL4Qf26moWfuBjMw3q9Q0kIYuiqNtgX0VBf2mCLrwAdHW1EM5MAflCHn/rotvAzd3UIejDbqQQDtfbyXjQdho54J3pg8QHofNl0B1DT33hYGbNLTY/q7c4qEwuT94Qghqoehk459qOZjT7M1S0+ADVdglhmAQji6RvdhZ/Fy3suUWWoylw23wz9Uuq8Dbb4AJRyszvAIk0CBu6v4SdtBJ1wkzyDde6Vehdfo2JE6kEbrIMAVGxEAynFXwIGTPjpDyGIIZrtBMrNhqFT2uz6brCOjsGfXQ4zQAxXfQpJ+OkXIYhBauE4fDz+vtP2fRcByE3LcAbT8HPuJeGnh6Yh6Mr2ZwFDsfZK46WIqSi2okMdBKBmM0CuwWCIqrbX1iz8+ADRVznFRSGIoajbYI2rQB1fjrp0LTAYmvrIdSoL4WcJ1CFo5+r2g65nG2ARckpfRRMpddoCOxdAb81Wxpe7sbQLQNNGNJInsXzW4/josyoEvT/9lAyrqiz3oyiazL+tj0YfbI7HfzyIDghA0GPTu3ciXo0lVVVDcjRw49adpf13h5VXH4cvj+qQf/aK5/G5reqvnQSghbfAipx+EQ2kc/EfAQB06mQOqGF4Sf97dGThASg3HILOj0p7gACgB3JK30YDqehuk/3iA1CKP0cTDRcpAgAtKZueBIuN6IhTYADA2Zxr2gKL9a4uRhWAAIAzGY/vTqLpDQ0vFVvRAQEIADi7xoPQuZO9WQIQAHBmDQehU1cnwToIQKlZqSx1NzAFAPxILhtVgLo6CdZFBchmVABYFblhYSPHQFpgOTf9jfplAAD90Pwk2EZ0oIvb4FWAAGBFnJwEa6SLo/AdVICalcpS5I0AAHqk4cXFrxwvvA22+AB0rmEFKKWNAAD6I8ckGkjppYWPt3QxBD2JZjoZlgIAflrTa67y4/iHWLAOAtArTWeA1kejkRAEAKshdbHiZuEBaDwe1wGoWQj6/nsBCAB6I01iyXS0CbrZHFD6T8VrAQD0Q/MVN/9bLFgnASjl1GhnQBe9QgDgWZZvxU0nAahM+S/RQErdrM0GAFZDNy2wHM0qQFFuBADQD02vw0jlEE6B1RrOAIUZIADojYY7/lJOv4gF6yYAHReN7w1xFB4AOKtuAtD585No7H9tBADAGXQSgGa7gBreG3L8kkFoAOBMOpoBan4U3kkwAOCsOgtAZcN7QyLFWwEAdO9xWrq53M4CUOSy2SB0NggNAL2QcqP349zBVRrdBaC12I9m1uPx99pgAMCpdRaAxuO7k2h6KWpKAhAAdC0VG9FAStHohoiz6K4CVMu5WRss5a0AAJZajoEFoJzSV9FAisIgNAB0Lm/E2eXGt8mfQbcVoLLcj2bWRx9e2goAoDNF46ss0sAC0LlXml6JYQ4IADpWlXCancrO5SQWrNMANNsI3exm+BTxTgAA3UmxEU2cOx5YBSimg0+N5oCqCtCWfUAA0KW0EY3850ksWOcBKMryfjR1fHQhAICFG21vNx5FOekILVT3AaiNOaBwLQYAdOJ82awLk6KNHHBqnQegaerLeT8aSCkuaIMBQAeOGx5GKhd/BL7WfQUopnNAX0QzrsUAgC6ktBFNpPRtdKAXAahKj/vRVCreCwBgoVLOr8XZ5cqfowO9CEDj27er/l+eRAPaYADQgab7+Jpei3VG/agARd0GS9pgALBETk6ANSs+dLADqNabANTGcfiU0scBACxG0egOsNrhePzHYVeAxp/e3a++NEuBliICwOIUaSua6Kj9VetPBSimp8E+j4ZSeTQKAGDuUtM9fB2dAKv1KgC1shU64loAAHN10nFpNnu7FvvRkV4FoFbaYBHrow8vbQUAMD+PH25FQzk/6uQIfK1fFaCYtsFuRkOGoQFgzprO/3Q4AF3rXQCKotyLpuph6EuXNgIAmIum8z85yq+iQ70LQOPx3UnTu8Fq6XxxMQCA1o1G0yJDwwWILdwC0UD/KkDRyt1gtWuOxAPAHBzHVjS19ng/OtTLABRrL+9FC8PQjsQDQPtSpKb3b3Y6/1PrZQAaj8eHbQxDhyoQALRq2v5KzQagu57/qfWzAlRrYxhaFQgA2tVC+yvF2r3oWG8DUFvD0KEKBACtaaH9FfnoWAXo5+Scr0dzqkAA0II22l91cWN8typydKzXAWi6GVoVCAD6oYX2V0snvRvrdQCq5ciNL0gNVSAAaKyVmxYe5Tbu/Wys9wFo/MndvWh+JL52zXZoADibqv21VUWgjWgixUEf2l+13gegWktH4teLlwp3hAHAGaQyXYyGctlKV6cVSxGAojg/jhaqQDnFRTfFA8DpzK6+aH76qy/tr9pSBKAWFyO6KR4ATqnIa9eiqZ6c/npiOSpAtZaqQNOb4q9cNhANAC8o5/JCNJWKveiRpQlA0ypQO3uBplUgx+IB4PlGVy5dbDz8XBUwcvFSL46/P7E8FaCoT4TdqapAeRLNrRfHDz8LAOBntTE6klPcrwsZ0SNLFYBquczvRwtyShcMRAPAs7VU/Yl4WLbSwWnT0gWgFrdDV+3I4jOtMAD4aa0cHOrZ8PMTSxeAam3NAlU2ivzQqTAA+JHWqj89G35+YikD0LQK1M4VGVWYSqPRh5ebT7cDwAppaW3M5MatP/Rm+eHTljIA1XLxcn2UvZWBqlSkz1yTAQAzo6vbH7dS/Ym0Gz21tAGozWPxUV+W+lJyKgyAwau3PqeI3Whu0rej709b2gBUmx2Lj4NogwWJAFDf+bUb7fi8b0ffn7bUAaiWy3InWlL1O284Gg/AUI1GH2y2cudXXf05Kveix5Y+ANUD0W3dE1abHo03DwTAAKXy3L1ox+d9PPr+tKUPQFPF+d2WNkTXNswDATA07Q0+97/6U1uJADQdiG5pQ/RUSlsfXbt8IwBgAFocfK539d3se/WnthoVoGi/FTbdD2QoGoABSGX6MtoxOTmg1HsrE4Cm2m2FGYoGYOW12PqKPu/9+bGVCkCtt8JiOhR9z1A0AKuozdZX9Hjr809ZrQpQ/NAKa/PW2fV0vvhSCAJgldSXgbfY+op8VL4dS2TlAlBtfOv2bls3xp/YqELQPTfHA7AqUvlw3FbrK6fYW4bB56etZACq5bVpK6zNDZSbqTz6UggCYNmdzP20sfCwNomHZZudl4VY2QA0Ht+d5LJ8N9q1WRwfOR4PwNIabX+w2eLcT9SDz8tW/amtbACqzWEeqC7zXfzoyrZFiQAsnenQ87nWtj3XO3/2l2nw+WkrHYBqc5gHEoIAWDp/HXpOG9GOw3jU7snrRVr5AFTLay+/2+Z+oOnfUwgCYIkUuR7hSBvRkqr6c30ZW19PDCIATfcDFbk+ntfmULQQBMBSqIeec46L0ZK69bUsG5+fZRABqDanoeiTEHTZEXkAeqkOP+0OPcdkmVtfTwwmANWmQ9E570TLckoXpkfkLUsEoEd2rm5fazn8xLKe+vqxQQWgWl2ya/tk2IlNG6MB6Iudq7+v9/y03aa6uaynvn5scAGoNj0ZFnke/wNuCEEAdG0WfvJetGty49btUayIQQag2o1bdy62fTz+RB2CHow+vHwhAGDB5hV+lu2ur+cZbACqzY7Hx0G0bz0V6d5H01XjALAYcwo/kcu8swpzP08bdACaHY8vW98R9ESO2P3o2uUbTogBMG+z+73aDz+V6+NP79yPFTPoAFSbHo+f7giaUwjKaZTKowfmggCYlzkcdX/i5o3p3OzqGXwAqs07BMXJXNDJRD4AtOaja7+/Mafwc5CL87uxogSgEwsIQet1abJuiQUANFSPV+xcufxlznkeJ7Pqoed361GRWFEC0FMWEIKmLbGdq9vfaYkBcFbTW93Lhw8ipa1o3/TE16oNPf+YAPQjiwhBMWuJfeeUGACnVa9ZSWXxoM2LTZ9ymB8/enfVw09NAPoJsxD08usxnyPyP6hPiakGAfCipvM+RboX07GK9tV3Zo5v/3Gu7319IQA9w+yI/Pm3U87zPvqnGgTAz6pbXnOc95nKOd6v78yMgUjBc42ubo+r36hrMX+D6LsyHFWFM0cDN27d9oxi8KYtryJ9FnOq+tSm4eeT23sxICpAL2B86/ZoTheo/tisGnRl+zNtMYBhq095fXRt+7N5trxqQww/NQHoBdUXqFalx51YgJziYn2pqr1BAMNUtby26lNeVTi5GHM01PBTU14+pdH2B5vp3Ll7c5q+/ymT6r9r98atP8zj9nqYKy0wOJ161qc4TjdySvO+UPuwCj87Qw0/NRWgU6qn4xdwTP5pG/UCxXr4TVsMYHVVHxiu1cfbFxJ+Hj96e8jhp+bTVQMLHI7+QcqxVz4qrxuUZhmoAMHzzdpd9ZBz2oj5c9jmhApQA9Ph6AXNBT1xMh9kUBpgydXBp67uV1WfL4WfxfPpqgWzleTpywXOBf1Vzvt5LV8fj4ezu4HloQIEf+/kPWO3es9Y2EGX6sP6fqy9vNJ3e52Wh0tL6uOKqXw4XuQf6B+ZGJambwQg+Ksugs+Jm9VraW4LFJeVh0vLRlcuXUypqG98n9vOhueYpBz75oToAwEITmZ8jtPHc7q49OccVpWf6+NP7oyDv+PhMgezlF/Ui6s2o0t1yTMVe6pCdEUAYqjqrkDEwyr4xLUOgk9tMr3UdCD3ep2Fh8scja5u71a/wX244+uwqgrdL9fKz80KsUgCEEMz3eNTFu9Vf/DrllMnnQDzPi/Gw2XOOh2Q/mmzFpkwxAIIQAzBbAb06L0qeVzoqNrzg/pkspbXi/FwWZDRlcujlOqdQWkj+mNaGcop7eej46/MDNE2AYhV1afQc0LL65Q8XBaowxMAL6p+4XyVi/J+xCsHyqc0JQCxSmYLC4vXehR6nriZi/O7ntmn4+HSgdGHly+kIm70rBr096ZD1OnbXFRf4/FkPPbJgtMRgFhmo9EHm6l86a2qUr6ZU9TXU3R1uvdZJrks3x9/apzhLDxcOtTTttjPOaxC0UEdiqrvv6sqRd+qFPFzBCCWwezE1v/aiOO1zZSKzeo591r1nKtP8fYt8DxN1achD5eOLUFb7EVMg1Gqd06k9OeYhqNcfc31C3NShaRDL9JhEoDog1nA+b4OM1XIiY0q5Pyi+v7VlPMvq8rO5hJ9CJ2d8Kp3+6j6NObh0hMrEoSe5/AkFFVSHZqEoo7ltcc782xt9j0A1bdvT+c5WC2pCjp//clGrAZLDVt2LuiF8Xh6AutiFYR2VzgIVZ/A0l9Lykn+7tzjtT6X+OcuR3499WuYFX6KdtccCEA9M5AgBMBzTNtdj/L7VpTMhwDUU4IQwDCZ81kMAajnBCGAYRB8FksAWhJPB6E4jq2U0scrNNwHMFiCTzcEoCVzEoT26h/1QsVI8V4VhpxiAVguhznF/TguPxd8uiEALbHxp3fuV1/uz47QF+9UnyNGqkIAvXaYI25GcX7sVFe3BKAVcFIVuln/GH14aSsV6WL1/VvCEEA/aHP1jwC0Yk5eXPWPEIYAulOHnpTS/Vyc/1y1p38EoBUmDAEsVp5dIv1VHJV79vf0mwA0EH8ThkaX6vtwtqog9M7JFtxBbwMGaOKHSs9R+YXQszwEoAF6+iRZ/fNZdah4rXoRX0j9vwEZoGN5Un2A/CKX5f0498qB9tZyEoB4ujpUD1LHaPuDzVhb20yp2KxC0WtCETBg9SWkB9Vz8NtcVu2tcy/vCzyrQQDi74xvT28H/5sbwqehqDi3EUUVhqpQVPW4N1JMb1wWjIAVkSc5Rx12/pxzeRCPYl9La3UJQLyQp0LR/ad/fTQarcfj7zfrW95TkX5Z/dKr1ael+ut6FZLWU+R1Q9dA9+q2VT2vE9XXNEkp/lL99LuqqvPneJwP4pVXDlV2hkUAopGTB8b+i/xnR5cubfzwk3OxEXTv3CsHMWSP0zgX5V6wWsrjwyjXZmFGsOEZUgDMyc7V7RwN3Lh12zMKmIsiAAAGRgACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAZHAAIABkcAAgAGRwACAAbnXADMSc55PwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADoi/8fEwKfvahgqTUAAAAASUVORK5CYII="
                    />
                  </defs>
                </svg>
              )}
              <Typography variant="body2" className="dropzone-text">
                {fileLimitReached
                  ? attachmentsAmountErrorMsg
                  : selectedFiles.length > 0
                  ? 'Please upload or remove the above file(s) before adding more'
                  : 'Click or Drag and drop a file here to upload. '}
              </Typography>
            </div>

            <input
              id="fileInput"
              type="file"
              disabled={fileLimitReached || selectedFiles.length > 0}
              ref={this.fileInputRef}
              className="invisibleFiles"
              onClick={(e: any) => {
                e.target.value = null;
              }}
              onChange={(e: any) => {
                this.selectFiles(e.target.files);
                this.props.onIncompleteFileUpload();
              }}
            />
            <br />
          </Box>
          {selectedFiles.length === 0 && (
            <Box>
              <Typography className="dropzone-helptext">
                File types accepted: JPG, JPEG, BMP, PDF, PNG, GIF. You can upload up to 10 files. Maximum size 20MB per
                file.
              </Typography>
            </Box>
          )}
        </Stack>
      </div>
    );
  }
}

interface RowProps {
  attachmentPk: string;
  downloadUrl: string;
  documentTypeRefData: ListData[];
  documentType: string;
  filename: string;
  filesize: number;
  btnTitle: string;
  loading: boolean;
  disableBtn: boolean;
  disableSelect: boolean;
  onClick?: (e: any) => void;
  changeDocument: (e: any) => void;
  removeDocument: (e: any) => void;
  progress: number;
  errors: string[];
  submittedToMosaiq: boolean;
  allowDelete: boolean;
  handleDateChange: (value: any) => void;
  encounterDate: Date;
  extraClass?: string;
}

class TableRow extends Component<RowProps> {
  // the basic tr element of the table showing uploaded file attachments
  private humanFileSize(bytes: number) {
    // convert raw bytes to a human readable string
    const thresh = 1024;
    if (Math.abs(bytes) < thresh) {
      return bytes + ' B';
    }
    const units = ['KB', 'MB', 'GB', 'TB'];
    let u = -1;
    do {
      bytes /= thresh;
      ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
  }

  public render(): JSX.Element {
    const {
      downloadUrl,
      filename,
      filesize,
      btnTitle,
      loading,
      disableBtn,
      disableSelect,
      onClick,
      progress,
      changeDocument,
      documentType,
      documentTypeRefData,
      errors,
      removeDocument,
      allowDelete,
      handleDateChange,
      encounterDate,
      extraClass,
    } = this.props;
    // @ts-ignores

    // Filter this down so the options are unique. Autocomplete experiences some jank if values aren't unique
    const documentTypeOptions = documentTypeRefData.map((data) => ({ label: data.name, value: data.id }));
    const uniqueValues = [...new Set(documentTypeOptions?.map((option) => option.value))];
    const uniqueOptions = uniqueValues
      .map((uniqueValue) => documentTypeOptions.filter((option) => option.value === uniqueValue)[0])
      .sort((a, b) => a.label.localeCompare(b.label));

    return (
      <tr className={classnames('minirego-attachment-row', extraClass)}>
        <td>
          <div>
            {allowDelete && (
              <IconButton onClick={removeDocument}>
                <CloseIcon />
              </IconButton>
            )}
          </div>
        </td>
        <td className="minirego-file-name">
          <>
            {downloadUrl ? (
              <a className="minirego-download-link" href={downloadUrl}>
                {filename}
              </a>
            ) : (
              filename
            )}
            <br />
            <ErrorInfo type="table-error" errors={errors} />
          </>
        </td>
        <td>{this.humanFileSize(filesize)}</td>
        <td>
          <BaseAutocomplete
            value={getOptionByValue(uniqueOptions, documentType)}
            fullWidth
            id="miniRegoAttachmentType"
            onChange={(option: SelectOptionType | string) => {
              const value = typeof option === 'string' ? option : option?.value;
              changeDocument(value);
            }}
            placeholder="Select"
            options={uniqueOptions}
          />
        </td>
        <td>
          <BaseDatePicker
            id="clinicDateSelector"
            readOnly={disableSelect}
            disableFuture
            value={dayjs(encounterDate)}
            onChange={(date: Dayjs | null): void => {
              handleDateChange(date);
            }}
          />
        </td>
        <td>
          <div>
            <LinearProgress color="primary" variant="determinate" value={progress} />
          </div>
        </td>
        <td>
          <div>
            <LoadingButton variant="outlined" loading={loading} disabled={disableBtn} onClick={onClick}>
              {btnTitle}
            </LoadingButton>
          </div>
        </td>
      </tr>
    );
  }
}

interface InnerProps extends WithApolloClient<{}> {
  file: File;
  fileLimitReached?: boolean;
  documentTypeRefData: ListData[];
  handleFileDelete: (file: File) => void;
  refetchCallback: () => void;
  documentType: string;
  key: number;
  patient: any;
  user: any;
  fileUploaded: boolean;
  onCloseUploadErrorModal: any;
  onOpenUploadErrorModal: boolean;
  uploadInfoErrorModalOpen: boolean;
}

interface InnerState {
  fields: any;
  documentType: string;
  url: string;
  progress: number;
  loading: boolean;
  errors: string[];
  encounterDate: Date;
  hasBeganUpload?: boolean;
  onOpenUploadErrorModal: boolean;
}

class Uploader extends Component<InnerProps, InnerState> {
  private region = REACT_APP_REGION;
  public constructor(props: InnerProps) {
    super(props);
    const { file } = this.props;

    const extension = file.name.split('.')[file.name.split('.').length - 1];
    const errors = [];

    if (extension) {
      const error = checkFileExtension(extension);
      if (error) {
        errors.push(error);
      }
    }

    this.state = {
      progress: 0,
      documentType: props.documentType.toString(),
      loading: false,
      fields: [],
      errors,
      url: '',
      encounterDate: new Date(),
      onOpenUploadErrorModal: Boolean(this.props.onOpenUploadErrorModal),
    };
  }

  private closeUploadInfoErrorModal = (): void => {
    this.setState({ ...this.state, onOpenUploadErrorModal: false });
  };

  private openUploadInfoErrorModal = (): void => {
    this.setState({ ...this.state, onOpenUploadErrorModal: true });
  };

  private handleBlockedNavigation = () => {
    this.openUploadInfoErrorModal();
    return false;
  };

  private uploadFile(url: string, fields: any) {
    this.setState({ ...this.state, loading: true });
    const { file, refetchCallback, handleFileDelete } = this.props;
    const { documentType, encounterDate } = this.state;
    const xhr = new XMLHttpRequest();
    const formData = new FormData();

    const parsedPayload = JSON.parse(fields.awsPayload);
    for (const [key, value] of Object.entries(parsedPayload.fields)) {
      formData.append(key, String(value));
    }
    formData.append('Content-Type', file.type);
    formData.append('file', file);

    xhr.upload.addEventListener('progress', (e) => {
      const progress = (e.loaded / e.total) * 100;
      this.setState({ ...this.state, progress });
    });
    xhr.addEventListener('load', () => {
      const { patient, user, client } = this.props;
      const variables = {
        awsKey: fields.awsKey,
        patientPk: patient.id,
        userPk: user.id,
        isRegistrationAttachment: true,
        documentType,
        encounterDate,
      };

      const statusCode = xhr.status;
      if ([200, 201, 204].includes(statusCode)) {
        client
          ?.mutate({
            mutation: CreateUKAttachmentMutation,
            variables,
          })
          .then(async function () {
            await refetchCallback();
            handleFileDelete(file);
          })
          .catch(() => {
            this.setState({ ...this.state, errors: ['An unknown error occurred'], loading: false });
          });
      } else {
        this.setState({ ...this.state, errors: ['An unknown error occurred'], loading: false });
      }
    });
    xhr.addEventListener('error', () => {
      this.setState({ ...this.state, errors: ['An unknown error occurred'], loading: false });
    });

    xhr.open('POST', url);
    xhr.send(formData);
  }

  public render(): JSX.Element {
    const { file, documentTypeRefData, handleFileDelete } = this.props;
    const { documentType, encounterDate } = this.state;
    if (!file.name) {
      // TODO Added logging for Sentry issue OP-PROD-1WS "$fileName" of required type "String!"
      logger.error(`RORegAttachments.tsx may be attempting to upload a file without a name. File ${file}`);
    }
    const variables = {
      fileName: file.name,
    };
    const handleDateChange = (encounterDate: Date) => {
      this.setState({ encounterDate: encounterDate });
    };

    return (
      <Query query={GET_UPLOAD_DETAILS_QUERY} variables={variables}>
        {({ loading, data }: any): JSX.Element => {
          if (data && data.fileUploadPayload) {
            const { fields, url } = data.fileUploadPayload;

            if (file.size > 20971520) {
              // 20971520 = 20Mb
              if (this.state.errors.indexOf(fileSizeErrorMsg) === -1) {
                this.setState({ ...this.state, errors: [...this.state.errors, fileSizeErrorMsg] });
              }
            }

            const { loading, progress, errors } = this.state;
            const downloadUrl = this.state.url;
            if (this.state && !this.state.fields) {
              this.setState({ ...this.state, fields, url });
            }

            const handleDocumentTypeChange = (e: string) => {
              /*if (e.target && e.target.value) {
                this.setState({ ...this.state, documentType: e.target.value });
              }*/
              if (e) {
                this.setState({ ...this.state, documentType: e });
              }
            };

            const uploadComplete = progress >= 100;

            let disableBtn = uploadComplete || errors.length > 0;
            if (this.region === Region.UK) {
              disableBtn = uploadComplete || !documentType || !encounterDate || errors.length > 0;
            }

            const attachmentPk = `${file.name}-${file.lastModified}`;

            return (
              <>
                <Prompt message={this.handleBlockedNavigation}></Prompt>
                <ModalInfo
                  isOpen={this.state.onOpenUploadErrorModal}
                  title={FORGOT_UPLOAD_TITLE}
                  text={FORGOT_UPLOAD_TEXT}
                  extraClass="pending-submit-modal"
                  dismissFunction={(): void => {
                    this.closeUploadInfoErrorModal();
                  }}
                />
                <TableRow
                  extraClass="unattached-minirego-file"
                  attachmentPk={attachmentPk}
                  downloadUrl={downloadUrl}
                  errors={errors}
                  documentTypeRefData={documentTypeRefData}
                  documentType={documentType}
                  // @ts-ignore
                  changeDocument={handleDocumentTypeChange}
                  removeDocument={handleFileDelete}
                  submittedToMosaiq={false}
                  filename={file.name}
                  filesize={file.size}
                  encounterDate={encounterDate}
                  handleDateChange={handleDateChange}
                  btnTitle={uploadComplete ? 'Uploaded' : 'Upload'}
                  loading={loading}
                  disableBtn={disableBtn}
                  allowDelete={true}
                  disableSelect={progress >= 100}
                  onClick={(e: Event): void => {
                    e.preventDefault();
                    this.uploadFile(url, fields);
                  }}
                  progress={progress}
                />
              </>
            );
          }
          if (loading) {
            return (
              <tr>
                <td>loading</td>
              </tr>
            );
          }
          return <div></div>;
        }}
      </Query>
    );
  }
}

const ApolloUploader = withApollo<InnerProps>(Uploader);

const apolloComponent = withApollo<Props>(RORegAttachments);
export default apolloComponent;
